import { type Brand } from '@/consts/brands';
import { get, post, httpDelete, patch } from '@/utils/http/client';
import { urls } from '@/utils/urls';

import { type VisitMetadata, type Visit } from './types';

const PHOTO_UPLOAD_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes; uploads can be slow

const getVisit = async ({ uuid, brand }: { uuid: string; brand?: Brand }) => {
  // When brand is known, use the brand-aware URL (avoids cross-origin issues in multi-brand setups).
  // Fall back to a relative URL so the browser resolves against the current origin automatically.
  const url = brand ? urls[brand].api.visits.show(uuid) : `/api/visits/${uuid}`;
  const response = await get(url);

  return response.data as Visit;
};

const storeVisitPhoto = async ({
  brand,
  data,
  uuid,
}: {
  uuid: string;
  brand: Brand;
  data: FormData;
}) => {
  const response = await post<Visit>(
    urls[brand].api.visits.photos.store(uuid),
    data,
    { timeout: PHOTO_UPLOAD_TIMEOUT_MS },
  );

  return response.data;
};

const destroyVisitPhoto = async ({
  brand,
  photoId,
  uuid,
}: {
  uuid: string;
  brand: Brand;
  photoId: number;
}) => {
  const response = await httpDelete(
    urls[brand].api.visits.photos.destroy(uuid, photoId),
  );

  return response.data as Visit;
};

const updateVisitOrder = async ({
  brand,
  uuid,
  orderId,
  addShopifyProductVariantId,
  removeShopifyProductVariantId,
}: {
  brand: Brand;
  uuid: string;
  orderId: number;
  addShopifyProductVariantId: string;
  removeShopifyProductVariantId: string;
}) => {
  await patch(urls[brand].api.visits.orders.update({ uuid, orderId }), {
    add_shopify_product_variant_id: addShopifyProductVariantId,
    remove_shopify_product_variant_id: removeShopifyProductVariantId,
  });
};

const editSessionBag = async ({
  uuid,
  brand,
  sessionBag,
}: {
  uuid: string;
  brand: Brand;
  sessionBag: {
    [key: string]: any;
  };
}) => {
  await post(urls[brand].api.visits.editSessionBag(uuid), {
    sessionBag,
  });
};

const storeResponses = async ({
  visit,
  context,
  responses,
}: {
  visit: VisitMetadata;
  context: string;
  responses: {
    questionId: number;
    response: string | string[];
  }[];
}) => {
  await post(urls[visit.brand].api.visits.responses.store(visit.uuid), {
    context,
    responses,
  });
};

export {
  getVisit,
  storeVisitPhoto,
  destroyVisitPhoto,
  updateVisitOrder,
  editSessionBag,
  storeResponses,
};
